import React, { useEffect, useMemo, useCallback } from 'react';
import { useQueryLoader } from 'react-relay';
import Suspense from '@atlassian/jira-placeholder/src/index.tsx';
import main_getArchivedIssuesForProjectQuery, {
	type main_getArchivedIssuesForProjectQuery as FetchArchivedIssues,
} from '@atlassian/jira-relay/src/__generated__/main_getArchivedIssuesForProjectQuery.graphql';
import { NativeIssueTableSkeleton } from '@atlassian/jira-skeletons/src/ui/native-issue-table/components/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { usePathParam } from '@atlassian/react-resource-router';
import { NUMBER_OF_ISSUES_PER_PAGE } from '../common/ui/constants.tsx';
import type { AvatarOption, SelectOption, DateValue } from '../common/ui/types.tsx';
import { useArchiveIssueActions } from '../controllers/main.tsx';
import { useArchiveIssueStates } from '../controllers/selectors.tsx';
import { ArchivesTable } from './archives-table/main.tsx';

const Archives = () => {
	const cloudId = useCloudId();
	const [queryReference, loadQuery] = useQueryLoader<FetchArchivedIssues>(
		main_getArchivedIssuesForProjectQuery,
	);
	const [projectKey] = usePathParam('projectKey');
	const { filters, currentPage, searchBy, refetchArchivesIssuesData } = useArchiveIssueStates();
	const { setRefetchArchivesIssuesData } = useArchiveIssueActions();
	const selectedIssueTypes = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(filters?.issueType as Array<SelectOption>)?.map((issueType: SelectOption) => ({
				issueTypeId: issueType?.id || '',
			})) || [],
		[filters],
	);
	const selectedReporters = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(filters?.reporter as Array<AvatarOption>)?.map(
				(reporter: AvatarOption) => `ari:cloud:identity::user/${reporter.value}`,
			) || [],
		[filters],
	);
	const selectedArchivedBy = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(filters?.archivedBy as Array<AvatarOption>)?.map(
				(archivedBy: AvatarOption) => `ari:cloud:identity::user/${archivedBy.value}`,
			) || [],
		[filters],
	);
	const archivalDateRange = useMemo(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		() => (filters?.archivedDate as DateValue)?.value || {},
		[filters],
	);

	const first = currentPage * NUMBER_OF_ISSUES_PER_PAGE;
	const last = NUMBER_OF_ISSUES_PER_PAGE;

	const fetchData = useCallback(() => {
		if (projectKey && cloudId) {
			loadQuery(
				{
					projectKey,
					cloudId,
					selectedIssueTypes,
					selectedArchivedBy,
					selectedReporters,
					archivalDateRange,
					first,
					last,
					searchBy,
				},
				{
					fetchPolicy: 'network-only',
				},
			);
		}
	}, [
		archivalDateRange,
		cloudId,
		first,
		last,
		loadQuery,
		projectKey,
		searchBy,
		selectedArchivedBy,
		selectedIssueTypes,
		selectedReporters,
	]);
	useEffect(() => {
		if (refetchArchivesIssuesData) {
			setRefetchArchivesIssuesData(false);
		}
		fetchData();
	}, [fetchData, refetchArchivesIssuesData, setRefetchArchivesIssuesData]);

	return (
		<Suspense name="archives-main" fallback={<NativeIssueTableSkeleton />}>
			{queryReference && <ArchivesTable queryReference={queryReference} />}
		</Suspense>
	);
};

export default Archives;
